import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Layers, Store, Send, Building2, Landmark } from "lucide-react";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";

const solutions = [
  {
    id: "global-businesses",
    icon: Globe2,
    title: "Global Businesses",
    img: "photo-1522202176988-66273c2fd55f",
    alt: "A diverse African team working together in an office",
    problem: "Expanding into new markets usually means stitching together local payment methods, accounts and providers.",
    solution: "Swiftwire gives global businesses one platform to collect, hold, convert and send money across markets — without rebuilding the payment stack every time.",
    products: ["Payments", "Multi-Currency Accounts", "FX", "Payouts"],
  },
  {
    id: "fintechs",
    icon: Layers,
    title: "Fintechs",
    img: "photo-1531482615713-2afd69097998",
    alt: "Two women working together at a computer",
    problem: "Building financial experiences requires reliable payment infrastructure underneath the product.",
    solution: "Swiftwire provides connected payment and money movement infrastructure that fintechs can build on through APIs.",
    products: ["APIs", "Accounts", "Payouts", "Webhooks"],
  },
  {
    id: "marketplaces",
    icon: Store,
    title: "Marketplaces",
    img: "photo-1573164713988-8665fc963095",
    alt: "A professional African woman working on a laptop",
    problem: "Marketplaces need to collect from buyers and move funds to sellers and partners, often across borders.",
    solution: "Swiftwire connects collections and payouts in one platform, with clear tracking for every movement of funds.",
    products: ["Payments", "Payouts", "Beneficiary Management", "FX"],
  },
  {
    id: "remittance",
    icon: Send,
    title: "Remittance Companies",
    img: "photo-1531384441138-2736e62e0919",
    alt: "A smiling man in a colourful shirt",
    problem: "Cross-border money movement involves multiple currencies, corridors and settlement steps.",
    solution: "Swiftwire simplifies cross-border transfers, currency conversion and settlement through one connected platform.",
    products: ["Payouts", "FX", "Multi-Currency Accounts", "APIs"],
  },
  {
    id: "platforms",
    icon: Building2,
    title: "Platforms",
    img: "photo-1543269865-cbf427effbad",
    alt: "A diverse team collaborating around laptops",
    problem: "Platforms want payments and money movement embedded in their product, not bolted on.",
    solution: "Swiftwire's APIs let platforms embed collections, accounts, FX and payouts directly into their own experience.",
    products: ["APIs", "Payments", "Accounts", "Webhooks"],
  },
  {
    id: "enterprises",
    icon: Landmark,
    title: "Enterprises",
    img: "photo-1531206715517-5c0ba140b2b8",
    alt: "A diverse team joining hands together",
    problem: "Enterprise financial operations are spread across regions, currencies, teams and tools.",
    solution: "Swiftwire centralizes collections, payouts, transfers and financial operations with complete visibility and control.",
    products: ["Multi-Currency Accounts", "Payouts", "FX", "Statements"],
  },
];

const Solutions = () => (
  <div data-testid="solutions-page">
    <section className="sw-grid-bg border-b border-[#E2E8F0] bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Solutions</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            Payments that fit the way you do business.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#334155] md:text-lg">
            Whether you sell across borders, move money for customers, or build financial products — Swiftwire adapts to the way you do business in Africa and beyond.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
        {solutions.map((s, i) => (
          <Reveal key={s.id} delay={0.05}>
            <div
              id={s.id}
              data-testid={`solution-section-${s.id}`}
              className="group grid scroll-mt-24 gap-8 rounded-3xl border border-[#E2E8F0] bg-white p-7 shadow-sm transition-shadow duration-300 hover:shadow-lg hover:shadow-slate-900/[0.06] sm:p-10 lg:grid-cols-[1fr_1.4fr] lg:gap-14"
            >
              <div>
                <div className="-mx-7 -mt-7 mb-6 h-44 overflow-hidden rounded-t-3xl sm:-mx-10 sm:-mt-10 sm:mb-8">
                  <img
                    src={`https://images.unsplash.com/${s.img}?w=1000&h=420&q=80&auto=format&fit=crop&crop=faces`}
                    alt={s.alt}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </div>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <s.icon size={22} strokeWidth={1.5} />
                </span>
                <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-[#0F172A] md:text-3xl">{s.title}</h2>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.products.map((p) => (
                    <span key={p} className="rounded-full bg-[#F1F5F9] px-3 py-1 text-xs font-medium text-[#334155]">{p}</span>
                  ))}
                </div>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">The challenge</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#334155]">{s.problem}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#2E7CD6]">The Swiftwire way</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#334155]">{s.solution}</p>
                </div>
                <div className="sm:col-span-2">
                  <Link
                    to="/contact"
                    data-testid={`solution-${s.id}-cta`}
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A6E]"
                  >
                    Talk to us about {s.title.toLowerCase()}
                    <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <CtaSection title="Let's design your money movement." subtitle="Tell us how your business moves money — we'll show you how Swiftwire fits." />
  </div>
);

export default Solutions;
